import { Button, Flex, Text } from '@lideralia/alife-uikit';
import { CardPayInfo } from '@lideralia/alife-uikit/dist/organisms/Cards';
import React, { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { COUNTRIES_LIST } from '../../../utilities';

const ServiceProductJewelryStoreDetail = ({
  product,
  index,
  stripeKey,
  stripeAccount,
  company,
  locale,
  userCountry,
  handleSubmit,
  buttonDisabled,
  showPayment,
  setShowPayment,
}) => {
  const [productVariant, setProductVariant] = useState('');
  const [dedicatoryText, setDedicatoryText] = useState('');

  const dedicatoryMaxLength =
    product.max_dedicatory_text_length > 0 ? product.max_dedicatory_text_length : null;
  const showDedicatory = !!product.allow_dedicatory_text && !!dedicatoryMaxLength;

  const selectedVariant =
    product.variable_pricings?.find((vp) => String(vp.uuid) === String(productVariant)) || null;
  const displayTotal = selectedVariant ? selectedVariant.total : product.total;
  const displayCurrency = product.currency;
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const touchStartX = useRef(null);
  const intl = useIntl();

  const faqItems = (product.faqs || []).map((faq, i) => ({
    id: `faq-${i}`,
    title: faq.title,
    content: faq.description,
  }));

  const accordionItems = [
    {
      id: 'description',
      title: intl.formatMessage({ id: 'serviceProducts.accordionDescription' }),
      content: product.description,
    },
    ...faqItems,
  ].filter((item) => item.content !== null && item.content !== undefined);

  const [openAccordion, setOpenAccordion] = useState('description');

  let images = [];
  if (product.images?.length > 0) {
    images = product.images;
  } else if (product.image) {
    images = [{ id: 0, url: product.image }];
  }

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      if (diff > 0) {
        handleNextImage();
      } else {
        handlePrevImage();
      }
    }
    touchStartX.current = null;
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  return (
    <Flex flexDirection="column" rowGap="16px">
      {!showPayment ? (
        /* Step 1: Product detail with slider */
        <Flex flexDirection="column" style={{ gap: '12px' }}>
          {/* Image slider */}
          {images.length > 0 && (
            <div
              style={{
                position: 'relative',
                width: '100%',
                borderRadius: '12px',
                overflow: 'hidden',
              }}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              {/* Sliding strip */}
              <div
                style={{
                  display: 'flex',
                  width: `${images.length * 100}%`,
                  transform: `translateX(-${(currentImageIndex * 100) / images.length}%)`,
                  transition: 'transform 0.35s ease',
                }}
              >
                {images.map((img, i) => (
                  <img
                    key={img.id || `slide-${i}`}
                    src={img.url}
                    alt={`${product.name} ${i + 1}`}
                    style={{
                      width: `${100 / images.length}%`,
                      aspectRatio: '375 / 300',
                      objectFit: 'cover',
                      display: 'block',
                      flexShrink: 0,
                    }}
                  />
                ))}
              </div>
              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={handlePrevImage}
                    style={{
                      position: 'absolute',
                      left: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'rgba(255,255,255,0.85)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '36px',
                      height: '36px',
                      cursor: 'pointer',
                      fontSize: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    aria-label="Imagen anterior"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    onClick={handleNextImage}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'rgba(255,255,255,0.85)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '36px',
                      height: '36px',
                      cursor: 'pointer',
                      fontSize: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    aria-label="Imagen siguiente"
                  >
                    ›
                  </button>
                  {/* Dots — overlaid at bottom of image */}
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '10px',
                      left: 0,
                      right: 0,
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '6px',
                    }}
                  >
                    {images.map((img, i) => (
                      <span
                        key={img.id || `dot-${i}`}
                        style={{
                          display: 'inline-block',
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor:
                            i === currentImageIndex
                              ? company?.style?.primaryColor || '#007AFF'
                              : 'rgba(255,255,255,0.7)',
                        }}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Name + Variant row */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* No variants: name and price on the same row */}
            {!product.variable_pricings?.length ? (
              <Flex alignItems="center" justifyContent="space-between" gap="12px">
                <Text color="black96" sizeText="display16" fontWeight="bold" flex="1">
                  {product.name}
                </Text>
                {product.total > 0 && (
                  <Text
                    sizeText="display16"
                    fontWeight="bold"
                    style={{
                      color: company?.style?.primaryColor || '#007AFF',
                      flexShrink: 0,
                      width: '100px',
                      textAlign: 'right',
                    }}
                  >
                    {product.total}
                    {product.currency}
                  </Text>
                )}
              </Flex>
            ) : (
              /* With variants: name alone, then label + selector + price */
              <>
                <Text color="black96" sizeText="display16" fontWeight="bold">
                  {product.name}
                </Text>
                <div>
                <Text color="black60" sizeText="display14" style={{ flexShrink: 0 }}>
                    {product.variant_label || (
                      <FormattedMessage id="serviceProducts.labelVariant" />
                    )}
                </Text>
                <Flex alignItems="center" gap="12px">
                  <select
                    value={productVariant}
                    onChange={(e) => setProductVariant(e.target.value)}
                    style={{
                      flex: 1,
                      height: '44px',
                      border: '1.5px solid #e0e0e0',
                      borderRadius: '12px',
                      padding: '0 12px',
                      fontSize: '16px',
                      color: '#333',
                      backgroundColor: 'white',
                      appearance: 'none',
                      backgroundImage:
                        "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23999' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E\")",
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 12px center',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="" disabled>
                      —
                    </option>
                    {product.variable_pricings.map((v) => {
                      const val = typeof v === 'object' ? v.uuid ?? v.id ?? v.value : v;
                      const label = typeof v === 'object' ? v.label ?? v.value ?? v.name : v;
                      return (
                        <option key={val} value={val}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                  <Text
                    sizeText="display16"
                    fontWeight="bold"
                    style={{
                      color: company?.style?.primaryColor || '#007AFF',
                      flexShrink: 0,
                      width: '100px',
                      textAlign: 'right',
                      visibility: displayTotal > 0 && productVariant ? 'visible' : 'hidden',
                    }}
                  >
                    {displayTotal}
                    {displayCurrency}
                  </Text>
                </Flex>
                </div>
              </>
            )}
          </div>

          {/* Dedicatory text */}
          {showDedicatory && (
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                padding: '16px',
              }}
            >
              <Flex alignItems="center" gap="8px" marginBottom={2}>
                <Text sizeText="display24" color="black36" style={{ lineHeight: 1 }}>
                  &#8220;
                </Text>
                <Text color="black96" sizeText="display14" fontWeight="bold">
                  <FormattedMessage id="serviceProducts.labelDedicatory" />{' '}
                  <Text sizeText="display14" as="span">
                    (<FormattedMessage id="serviceProducts.optional" />)
                  </Text>
                </Text>
              </Flex>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={dedicatoryText}
                  onChange={(e) => setDedicatoryText(e.target.value.slice(0, dedicatoryMaxLength))}
                  maxLength={dedicatoryMaxLength}
                  placeholder={intl.formatMessage({ id: 'serviceProducts.dedicatoryPlaceholder' })}
                  style={{
                    width: '100%',
                    border: '1.5px solid #e0e0e0',
                    borderRadius: '12px',
                    padding: '10px 44px 10px 12px',
                    fontSize: '16px',
                    color: '#333',
                    boxSizing: 'border-box',
                    outline: 'none',
                    fontFamily: 'inherit',
                    height: '44px',
                  }}
                />
                <Text
                  sizeText="display12"
                  color="black36"
                  style={{
                    position: 'absolute',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    right: '12px',
                  }}
                >
                  {dedicatoryMaxLength - dedicatoryText.length}
                </Text>
              </div>
            </div>
          )}

          {/* Place order button */}
          <Button
            backgroundColor={company?.style?.primaryColor || '#007AFF'}
            color={company?.style?.textColor || 'white'}
            variant="primary"
            width="100%"
            onClick={() => {
              setShowPayment(true);
              window.scrollTo(0, 0);
            }}
            disabled={buttonDisabled || (product.variable_pricings?.length > 0 && !productVariant)}
          >
            <FormattedMessage id="serviceProducts.placeOrder" />
          </Button>

          {/* Accordion */}
          <div>
            {accordionItems.map((item, i) => (
              <div key={item.id}>
                {i > 0 && <div style={{ height: '1px', backgroundColor: '#e5e5e5' }} />}
                <button
                  type="button"
                  onClick={() => setOpenAccordion(openAccordion === item.id ? null : item.id)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'none',
                    border: 'none',
                    padding: '16px 0',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <Text color="black96" sizeText="display14" fontWeight="bold">
                    {item.title}
                  </Text>
                  <span
                    style={{
                      fontSize: '20px',
                      color: '#333',
                      lineHeight: 1,
                      flexShrink: 0,
                      marginLeft: '8px',
                    }}
                  >
                    {openAccordion === item.id ? '−' : '+'}
                  </span>
                </button>
                {openAccordion === item.id && item.content && (
                  <div style={{ paddingBottom: '16px' }}>
                    <Text color="black60" sizeText="display14" lineHeight={1.6}>
                      {item.content.split('\n').map((line) => (
                        <React.Fragment key={`acc-${item.id}-${line}`}>
                          {line}
                          <br />
                        </React.Fragment>
                      ))}
                    </Text>
                  </div>
                )}
                {i === accordionItems.length - 1 && (
                  <div style={{ height: '1px', backgroundColor: '#e5e5e5' }} />
                )}
              </div>
            ))}
          </div>
        </Flex>
      ) : (
        /* Step 2: Payment form */
        <Flex flexDirection="column" style={{ gap: '12px' }}>
          {/* Brief product summary */}
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              padding: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '8px',
                backgroundColor: '#f5f5f5',
                flexShrink: 0,
                overflow: 'hidden',
              }}
            >
              {product.image && (
                <img
                  src={product.image}
                  alt={product.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              )}
            </div>
            <Flex flexDirection="column" flex="1" gap="4px">
              <Text color="black96" sizeText="display14" fontWeight="bold">
                {product.name}
              </Text>
              {productVariant && (
                <Text color="black60" sizeText="display12">
                  <Text as="span" color="black84" sizeText="display12" fontWeight="medium">
                    {product.variant_label || (
                      <FormattedMessage id="serviceProducts.labelVariant" />
                    )}
                    {': '}
                  </Text>
                  {selectedVariant ? selectedVariant.name : productVariant}
                </Text>
              )}
              {dedicatoryText && (
                <Text sizeText="display12">
                  <Text as="span" color="black84" sizeText="display12" fontWeight="medium">
                      <FormattedMessage id="serviceProducts.labelDedicatory" />
                    {': '}
                  </Text>
                  <Text as="span" color="black60" sizeText="display12">
                    &ldquo;{dedicatoryText}&rdquo;
                  </Text>
                </Text>
              )}
              {displayTotal > 0 && (
                <Flex alignItems="center" justifyContent="space-between" width="100%" gap="4px">
                  <Text color="black60" sizeText="display14" fontWeight="bold">
                    <FormattedMessage id="serviceProducts.priceTotal" />
                  </Text>
                  <Text
                    sizeText="display14"
                    fontWeight="bold"
                    style={{ color: company?.style?.primaryColor || '#007AFF' }}
                  >
                    {displayTotal}
                    {displayCurrency}
                  </Text>
                </Flex>
              )}
            </Flex>
          </div>

          {/* Payment fields */}
          <CardPayInfo
            id={`card-pay-info-${product.uuid || index}`}
            paddingTop={0}
            maxWidth="100%"
            labelPayMethod={<FormattedMessage id="flowerEcommerce.labelPayMethod" />}
            labelCreditCard={<FormattedMessage id="flowerEcommerce.labelCreditCard" />}
            labelCreditCardNumber={<FormattedMessage id="flowerEcommerce.labelCreditCardNumber" />}
            labelCreditCardExpDate={
              <FormattedMessage id="flowerEcommerce.labelCreditCardExpDate" />
            }
            labelCreditCardCvc={<FormattedMessage id="flowerEcommerce.labelCreditCardCvc" />}
            mathodSelected="card"
            stripeKey={stripeKey}
            stripeAccount={stripeAccount}
            locale={locale}
            labelInvoice={<FormattedMessage id="flowerEcommerce.labelInvoice" />}
            labelNeedInvoice={<FormattedMessage id="flowerEcommerce.labelNeedInvoice" />}
            labelValidEmail={<FormattedMessage id="flowerEcommerce.labelValidEmail" />}
            labelName={<FormattedMessage id="flowerEcommerce.labelName" />}
            name=""
            labelSurname={<FormattedMessage id="flowerEcommerce.labelSurname" />}
            surname=""
            labelDNI={<FormattedMessage id="flowerEcommerce.labelDNI" />}
            dni=""
            labelEmail={<FormattedMessage id="flowerEcommerce.labelEmail" />}
            email=""
            labelAddress={<FormattedMessage id="flowerEcommerce.labelAddress" />}
            address=""
            labelLocation={<FormattedMessage id="flowerEcommerce.labelLocation" />}
            location=""
            labelCP={<FormattedMessage id="flowerEcommerce.labelCP" />}
            cp=""
            labelProvince={<FormattedMessage id="flowerEcommerce.labelProvince" />}
            province=""
            labelCountry={<FormattedMessage id="flowerEcommerce.labelCountry" />}
            countryIso={userCountry || 'ES'}
            deliveryCountryIso={userCountry || 'ES'}
            invoiceRequested
            labelButton={<FormattedMessage id="flowerEcommerce.labelButton" />}
            optionsCountry={COUNTRIES_LIST}
            isDeliverable={!!product.is_deliverable}
            labelDelivery={<FormattedMessage id="serviceProducts.labelDelivery" />}
            labelDeliveryName={<FormattedMessage id="serviceProducts.labelDeliveryName" />}
            labelDeliverySurname={<FormattedMessage id="serviceProducts.labelDeliverySurname" />}
            labelDeliveryDNI={<FormattedMessage id="serviceProducts.labelDeliveryDNI" />}
            labelDeliveryEmail={<FormattedMessage id="serviceProducts.labelDeliveryEmail" />}
            labelDeliveryAddress={<FormattedMessage id="serviceProducts.labelDeliveryAddress" />}
            labelDeliveryLocation={<FormattedMessage id="serviceProducts.labelDeliveryLocation" />}
            labelDeliveryCP={<FormattedMessage id="serviceProducts.labelDeliveryCP" />}
            labelDeliveryProvince={<FormattedMessage id="serviceProducts.labelDeliveryProvince" />}
            labelDeliveryCountry={<FormattedMessage id="serviceProducts.labelDeliveryCountry" />}
            optionsDeliveryCountry={COUNTRIES_LIST}
            labelInvoiceWithSameDeliveryAddress={
              <FormattedMessage id="serviceProducts.labelInvoiceWithSameDeliveryAddress" />
            }
            customColor={company?.style?.primaryColor}
            customTextColor={company?.style?.textColor}
            buttonDisabled={buttonDisabled}
            handleSubmit={(content) =>
              handleSubmit({
                ...content,
                delivery_cif: content.delivery_nif_cif || null,
                service_product_variable_pricing_uuid: productVariant || null,
                service_product_dedicatory_text: dedicatoryText || null,
              })
            }
            labelTermsAndConditions={
              <FormattedMessage
                id="flowerEcommerce.validatingMyOrderReadTheConditionsInterflora"
                values={{
                  link: (...chunks) => (
                    <strong
                      style={{
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        color: company?.style?.primaryColor,
                      }}
                      role="button"
                      tabIndex={0}
                      onClick={(e) => e.preventDefault()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') e.preventDefault();
                      }}
                    >
                      {chunks}
                    </strong>
                  ),
                }}
              />
            }
          />
        </Flex>
      )}
    </Flex>
  );
};

export default ServiceProductJewelryStoreDetail;
